import { faPaintbrush } from "@fortawesome/pro-regular-svg-icons/faPaintbrush";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogActions, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';
import { FileThumbnaildGetDto, RequestLifeCycle } from "app/appModels";
import { MprcButton, MprcFormSubHeader, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from "components";
import { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { Delta, EmitterSource, QuillCommands, QuillUnprivilegedEditor } from "components/Quill";
import {
    AsyncUploadResult,
    CreateOrUpdatePostListExclusiveCommand,
    GetPostListExclusivePublicationDto,
    GetPostPublicationDto,
    GetPublicationAttachDto,
    PublicationApprovalStatus,
    PublicationAttachFileItem,
    PublicationAttachType,
    PublicationCreationSettingsResponse,
    PublicationType
} from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import LibUtils from "lib/LibUtils";
import LinkifyIt from "linkify-it";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { NIL, v4 as uuidv4 } from 'uuid';
import { ICreatePublicationProps } from "views/knowledge/publication/creation";
import {
    isPresentationAttach,
    PublicationAttachFileHandler,
    PublicationAttachView,
    PublicationEditor,
    PublicationMediaView, PublicationMediaViewData
} from "views/knowledge/publication/creation/components";
import { PublicationDialogContent } from "views/knowledge/publication/creation/components/PublicationDialogContent";
import { forceRichEditor } from "views/knowledge/publication/creation/CreatePost";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    description?: boolean;
    editJustification?: boolean;
}

interface IState {
    record: GetPostListExclusivePublicationDto;
    validation: IValidation;
    uploadingStatus: UploadingStatus;
    forceRichEditor: boolean,
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreatePostListExclusive = (props: ICreatePublicationProps) => {
    const { onClose } = props;
    const qlEditorId = `ql${new Date().getTime()}`;
    const minHeight = 200;
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();

    const initialContentRef = useRef<string>("");
    const editorRef = useRef<QuillCommands | null>(null);
    const config = useRef<PublicationCreationSettingsResponse | null>(null);

    const [step, setStep] = useState<number>(1);
    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
    const [richEditor, setRichEditor] = useState<boolean>(false);
    const [loadDto, setLoadDto] = useState<GetPostListExclusivePublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const metadataProcessing = useRef<string[]>([]);
    const requirePublicationEditJustification = useRef<boolean>(false);

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                content: "",
                title: "",
                attachs: [],
                publicationType: PublicationType.Post,
                listExclusiveId: null,
                listTitle: null,
                presentation: null,
                editJustification: null
            },
            validation: {
                title: undefined,
                description: undefined,
                editJustification: undefined
            },

            uploadingStatus: "none",
            forceRichEditor: false,
        });


    ///
    const updateRecord = (data: Partial<GetPostPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const validateDescription = (text: string, html: string, files: GetPublicationAttachDto[] | null) => {
        return (html != null && html != "<p><br></p>") || (files != null && files.length > 0);
    };

    ///
    const handleEditorChange = (content: string, delta: Delta, source: EmitterSource, editor: QuillUnprivilegedEditor) => {
        const text = editor.getText().trim();
        const html = editor.getHTML();

        setState({
            ...refState.current,
            record: updateRecord({ content: html }),
            validation: { ...refState.current.validation, description: validateDescription(text, html, state.record.attachs) }
        });

        analyse(content);
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: v }),
            validation: { ...state.validation, title: v.length > 0 }
        });
    };

    ///
    const handlePresentation = (data: PublicationMediaViewData) => {
        setState({
            ...state,
            record: updateRecord({ presentation: data.presentation, attachs: data.attachs })
        });
    };

    ///
    const handleFileUploaded = (e: AsyncUploadResult) => {
        const data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        const attachType: PublicationAttachType = (PublicationAttachType[e.mainFile.metadata.type as any]) as any;
        const attachs = refState.current.record.attachs.splice(0);

        var at = {
            attachId: uuidv4(),
            file: {
                fileId: data.mainFile.fileId,
                metadata: data.mainFile.metadata,
                mimetype: data.mainFile.mimetype,
                url: data.mainFile.url
            },
            thumbnail: data.thumbnail == null
                ? null
                : {
                    fileId: data.thumbnail.fileId,
                    metadata: data.thumbnail.metadata,
                    mimetype: data.thumbnail.mimetype,
                    url: data.thumbnail.url
                },
            attachType: attachType,
            isEmbed: false,
            linkMetadata: null,
            videoProcessed: false,
            caption: null,
        } as GetPublicationAttachDto;

        attachs.push(at);

        const presentation = addToPresentation(at);

        const newTitle = refState.current.record.title == "" ? LibUtils.getFilenameWithoutExtension(data.mainFile.metadata.filename) : refState.current.record.title;

        setState({
            ...refState.current,
            record: updateRecord({ attachs: attachs, presentation: presentation, title: newTitle }),
            validation: { ...state.validation, title: newTitle.length > 0 }
        });
    };

    ///
    const handleUploadingStatus = (status: UploadingStatus) => {
        setState({ ...refState.current, uploadingStatus: status });
    };

    ///
    const analyse = (text: string) => {
        const attachs = refState.current.record.attachs.slice(0) ?? [];
        const linkify = new LinkifyIt();
        const matches = linkify.match(text);

        if (matches == null || matches.length == 0)
            return;

        matches.forEach((m) => {
            const existsRequest = metadataProcessing.current.some(x => x == m.url);
            if (!existsRequest && attachs.find(p => p.linkMetadata != null && p.linkMetadata.url == m.url) == null) {
                metadataProcessing.current.push(m.url);
                api.v1.urlParser.getMetadata({ url: m.url })
                    .then((response) => {
                        if (response.status == 200) {
                            const metadata = response.data;
                            if (metadata.externalId == null || attachs.find(p => p.linkMetadata != null && p.linkMetadata.externalId == metadata.externalId) == null) {
                                const attachType: PublicationAttachType = (PublicationAttachType[metadata.provider as any]) as any;

                                var at = {
                                    attachId: uuidv4(),
                                    file: null,
                                    thumbnail: null,
                                    attachType: attachType,
                                    isEmbed: false,
                                    linkMetadata: metadata,
                                    videoProcessed: false,
                                    caption: null,
                                } as GetPublicationAttachDto;
                                attachs.push(at);

                                const presentation = addToPresentation(at);

                                setState({
                                    ...refState.current,
                                    record: updateRecord({ attachs: attachs, presentation: presentation })
                                });
                            }
                        }
                        else {
                            enqueueSnackbar(t("Publication.Common.NotIdentifyTypeMessage"), { variant: "error" });
                        }
                    })
                    .catch(() => {
                        enqueueSnackbar(t("Publication.Common.NotIdentifyTypeMessage"), { variant: "error" });
                    })
                    .finally(() => {
                        metadataProcessing.current = metadataProcessing.current.filter(x => x != m.url);
                    });
            }
        });
    };

    ///
    const addToPresentation = (attach: GetPublicationAttachDto) => {
        let presentation = refState.current.record.presentation;

        if (isPresentationAttach(attach.attachType)) {
            if (!presentation) {
                presentation = { orderedItems: [attach.attachId], rotationInterval: 0 };
            }
            else {
                presentation = { ...presentation, orderedItems: [...presentation.orderedItems, attach.attachId] };
            }
        }
        return presentation;
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null, exclusiveListId: props.list?.listExclusiveId ?? null })
            .then((result) => {
                config.current = result.data;
                requirePublicationEditJustification.current = config.current.defaultChannel.length == 0 ? false : config.current!.defaultChannel[0].requirePublicationEditJustification;
                setFormCycle(RequestLifeCycle.None);

                setState({
                    ...state,
                    validation: {
                        title: undefined,
                        description: undefined,
                        editJustification: !requirePublicationEditJustification.current
                    }
                });
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        Promise.all([
            api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null, exclusiveListId: props.list?.listExclusiveId ?? null }),
            api.v1.knowledgePublication.getPostListExclusivePublication({ publicationId }),
        ])
            .then((results) => {
                config.current = results[0].data;
                requirePublicationEditJustification.current = config.current.defaultChannel.length == 0 ? false : config.current!.defaultChannel[0].requirePublicationEditJustification;

                const record = results[1].data;
                setState({
                    uploadingStatus: "none",
                    forceRichEditor: forceRichEditor(record.content),
                    record: record,
                    validation: {
                        title: true,
                        description: true,
                        editJustification: !requirePublicationEditJustification.current
                    }
                });
                setLoadDto(LibUtils.deepCopy({ ...record, listExclusive: null }));
                initialContentRef.current = record.content ?? "";
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = () => {
        const { record } = state;

        const files: PublicationAttachFileItem[] = record.attachs.map((v) => ({
            attachId: v.attachId,
            fileId: v.file?.fileId ?? null,
            thumbnailId: v.thumbnail?.fileId ?? null,
            cardImageId: v.cardImage?.fileId ?? null,
            linkMetadata: v.linkMetadata,
            caption: v.caption,
            binderPageRefId: null,
        }));

        const dto: CreateOrUpdatePostListExclusiveCommand = {
            publicationId: record.publicationId,
            content: record.content,
            title: record.title,
            attachs: files,
            presentation: record.presentation,
            listExclusiveId: props.list!.listExclusiveId,
            contentUnformatted: null,
            threadId: null,
            editJustification: record.editJustification
        };

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.knowledgePublication.createOrUpdatePostListExclusivePublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);

                if (response.data.approvalStatus == PublicationApprovalStatus.Approved)
                    enqueueSnackbar(response.data.newPublicationId ? t("Publication.CreatePost.PublishMessage") : t("Publication.CreatePost.PublishUpdateMessage"), { variant: "success" });
                else
                    enqueueSnackbar(t("Publication.CreatePost.PublishModerationMessage"), { variant: "warning" });

            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const canSave = () => {
        const { validation, uploadingStatus, record } = state;
        return uploadingStatus != "uploading"
            && validation.title === true
            && (validation.description === true || record.attachs.length > 0)
            && recordChanged
            && (!requirePublicationEditJustification.current || validation.editJustification === true);
    };

    ///
    const canGoStep2 = () => {
        const { validation, uploadingStatus, record: { attachs } } = state;
        return (uploadingStatus != "uploading" && validation.title === true && (validation.description === true || attachs.length > 0));
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[StepFirstPage] = () => {
        const { record, validation, forceRichEditor, } = state;

        return (
            <>
                <PublicationDialogContent>

                    <TextField
                        autoFocus
                        fullWidth
                        name="title"
                        margin="normal"
                        placeholder={t("Publication.CreatePost.TitlePlaceholder")}
                        autoComplete="off"
                        tabIndex={0}
                        value={record.title}
                        error={validation.title === false}
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 },
                            }
                        }}
                        onChange={(e) => handleTitleChange(e)}
                    />

                    <PublicationEditor
                        id={qlEditorId}
                        minHeight={minHeight}
                        toolbar={richEditor || forceRichEditor}
                        initialValue={initialContentRef.current}
                        readOnly={false}
                        placeholder={t("Publication.CreatePost.EditorPlaceholder")}
                        onChange={handleEditorChange}
                        ref={editorRef}
                    />

                    <PublicationMediaView
                        data={
                            {
                                presentation: record.presentation,
                                attachs: record.attachs,
                            }
                        }
                        onChange={handlePresentation}
                        onUploading={(uploading) => handleUploadingStatus(uploading ? "uploading" : "none")}
                    />

                    <PublicationAttachView
                        attachs={record.attachs}
                        onChange={
                            (attachs) => setState({
                                ...state,
                                record: updateRecord({ attachs }),
                                validation: { ...state.validation, ...validation }
                            })
                        }
                    />

                    <PublicationAttachFileHandler
                        publicationId={record.publicationId}
                        attachs={record.attachs}
                        allowAttachVideo={config.current?.allowAttachVideo}
                        maxSize={config.current?.maxAttachSize ?? undefined}
                        onUploadComplete={handleFileUploaded}
                        onUploadStatusChange={handleUploadingStatus}
                        additionalLeftButton={
                            [
                                <IconButton key="richEditor" onClick={() => setRichEditor(!richEditor)} title={t("Publication.Common.Editor.FormatTooltip")}>
                                    <MprcText color={richEditor ? "highlight" : "inherit"}>
                                        <FontAwesomeIcon icon={faPaintbrush} fixedWidth />
                                    </MprcText>
                                </IconButton>
                            ]
                        }
                    />
                </PublicationDialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            <b>{t("Publication.CreatePost.Footer1")}</b>
                        </Grid>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => onClose(null)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>
                            {
                                requirePublicationEditJustification.current == true && recordChanged
                                    ? <MprcButton
                                        variant="contained"
                                        color="primary"
                                        disabled={!canGoStep2()}
                                        onClick={() => setStep(StepSecondPage)}
                                    >
                                        <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                                    </MprcButton>
                                    : recordChanged &&
                                    <MprcButton
                                        variant="contained"
                                        color="success"
                                        onClick={() => save()}
                                        disabled={!canSave()}
                                    >
                                        <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                                    </MprcButton>
                            }

                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };


    steps[StepSecondPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <PublicationDialogContent>
                    <MprcFormSubHeader
                        title={t("Publication.CreatePost.EditJustificationTitle") + " *"}
                        subtitle={t("Publication.CreatePost.EditJustificationSubtitle")}
                        margin="dense"
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        name="editJustification"
                        margin="dense"
                        variant="outlined"
                        rows={5}
                        placeholder={t("Publication.CreatePost.EditJustificationHint")}
                        required
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 1024 }
                            }
                        }}
                        value={record.editJustification ?? ""}
                        error={validation.editJustification == false}
                        onChange={(e) =>
                            setState({
                                ...refState.current,
                                record: updateRecord({ editJustification: e.target.value }),
                                validation: { ...refState.current.validation, editJustification: e.target.value.length > 0 }
                            })
                        }
                    />
                </PublicationDialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="end">
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => setStep(1)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (richEditor) editorRef.current?.focus();
    }, [richEditor]);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    // Ao carregar registro para edição
    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
        else
            loadSettings();
    }, []);

    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "90%", maxWidth: "90%" } } : undefined}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t(state.record.publicationId == NIL ? "Publication.CreatePost.TitleNew" : "Publication.CreatePost.TitleEdit")}
                        </Typography>
                        {state.record.listTitle != null && <Typography variant="bodyNotice">{t("Publication.CreatePost.PublicationExclusiveMessage")} <strong>{state.record.listTitle}</strong></Typography>}
                    </Grid>
                    <Grid>
                        <IconButtonExpandScreen
                            expanded={fullScreenMode}
                            size="small"
                            onClick={() => setFullScreenMode(!fullScreenMode)}
                        />
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
